import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User, Camera, Trash2, Loader2, MapPin, Phone, Calendar, Clock, Save, Check,
} from 'lucide-react';
import { API } from '../../App';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const WEEKDAYS = [
  { id: 'mon', short: 'SEG', label: 'Segunda' },
  { id: 'tue', short: 'TER', label: 'Terça' },
  { id: 'wed', short: 'QUA', label: 'Quarta' },
  { id: 'thu', short: 'QUI', label: 'Quinta' },
  { id: 'fri', short: 'SEX', label: 'Sexta' },
  { id: 'sat', short: 'SÁB', label: 'Sábado' },
  { id: 'sun', short: 'DOM', label: 'Domingo' },
];

/** Compress an image file to base64 (~640px max, JPEG q=0.82). Keeps payload small. */
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 640;
        let w = img.width;
        let h = img.height;
        if (w > h && w > MAX) { h = (h * MAX) / w; w = MAX; }
        else if (h > MAX) { w = (w * MAX) / h; h = MAX; }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const ProfessionalProfilePage = () => {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    photo_base64: '',
    contact_phone: '',
    contact_address: '',
    availability_days: [],
    availability_start: '08:00',
    availability_end: '18:00',
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/professional/me`);
        setProfile(data);
        setForm({
          photo_base64: data.photo_base64 || '',
          contact_phone: data.contact_phone || data.phone || '',
          contact_address: data.contact_address || (data.locations?.[0]?.address || ''),
          availability_days: data.availability_days || [],
          availability_start: data.availability_start || '08:00',
          availability_end: data.availability_end || '18:00',
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao carregar perfil');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handlePhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione um arquivo de imagem');
      return;
    }
    setUploadingPhoto(true);
    try {
      const b64 = await compressImage(file);
      setForm((f) => ({ ...f, photo_base64: b64 }));
      // Auto-save the photo immediately
      const { data } = await axios.patch(`${API}/professional/profile`, { photo_base64: b64 });
      setProfile(data.professional);
      toast.success('Foto atualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar foto');
    } finally {
      setUploadingPhoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      await axios.patch(`${API}/professional/profile`, { photo_base64: '' });
      setForm((f) => ({ ...f, photo_base64: '' }));
      setProfile((p) => ({ ...p, photo_base64: null }));
      toast.success('Foto removida');
    } catch (err) {
      toast.error('Erro ao remover foto');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const toggleDay = (id) => {
    setForm((f) => ({
      ...f,
      availability_days: f.availability_days.includes(id)
        ? f.availability_days.filter((d) => d !== id)
        : [...f.availability_days, id],
    }));
  };

  const selectAll = () =>
    setForm((f) => ({ ...f, availability_days: WEEKDAYS.map((d) => d.id) }));
  const selectWeekdays = () =>
    setForm((f) => ({ ...f, availability_days: ['mon', 'tue', 'wed', 'thu', 'fri'] }));
  const clearDays = () => setForm((f) => ({ ...f, availability_days: [] }));

  const handleSave = async () => {
    if (form.availability_start && form.availability_end &&
        form.availability_start >= form.availability_end) {
      toast.error('O horário final deve ser depois do inicial');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/professional/profile`, {
        contact_phone: form.contact_phone,
        contact_address: form.contact_address,
        availability_days: form.availability_days,
        availability_start: form.availability_start,
        availability_end: form.availability_end,
      });
      setProfile(data.professional);
      toast.success('Perfil atualizado com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const initials = (profile?.name || 'P')
    .split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase();

  return (
    <div className="max-w-3xl mx-auto" data-testid="professional-profile-page">
      {/* Header card with photo */}
      <Card className="p-6 mb-4 border-slate-200">
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
          {/* Photo */}
          <div className="relative">
            <div
              className="w-28 h-28 rounded-2xl bg-gradient-to-br from-blue-600 to-slate-900 flex items-center justify-center overflow-hidden shadow-md border-4 border-white ring-2 ring-slate-100"
              data-testid="profile-photo-wrapper"
            >
              {form.photo_base64 ? (
                <img src={form.photo_base64} alt="Foto de perfil" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {initials}
                </span>
              )}
              {uploadingPhoto && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoPick}
              className="hidden"
              data-testid="profile-photo-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute -bottom-2 -right-2 w-9 h-9 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
              data-testid="profile-photo-change-btn"
              title="Alterar foto"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>

          {/* Right side */}
          <div className="flex-1 text-center sm:text-left">
            <p className="text-[10px] uppercase tracking-widest font-bold text-blue-600 mb-0.5">
              PERFIL PROFISSIONAL
            </p>
            <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {profile?.name}
            </h1>
            <p className="text-sm text-slate-500 mb-3">{profile?.formation}</p>
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              {profile?.status === 'active' ? (
                <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  <Check className="w-3 h-3" /> Perfil verificado
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  Aguardando validação
                </span>
              )}
              {form.photo_base64 && (
                <button
                  onClick={handleRemovePhoto}
                  className="inline-flex items-center gap-1 text-red-600 text-[10px] font-bold uppercase tracking-wide hover:underline"
                  data-testid="profile-photo-remove-btn"
                >
                  <Trash2 className="w-3 h-3" /> Remover foto
                </button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Contact + Address */}
      <Card className="p-5 mb-4 border-slate-200">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-blue-600" /> Localização & Contato
        </h3>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1">
              <Phone className="w-3.5 h-3.5" /> Telefone / WhatsApp
            </Label>
            <Input
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="(00) 00000-0000"
              className="border-slate-300 focus:border-blue-500"
              data-testid="profile-phone-input"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1">
              <MapPin className="w-3.5 h-3.5" /> Endereço de atendimento
            </Label>
            <Input
              value={form.contact_address}
              onChange={(e) => setForm({ ...form, contact_address: e.target.value })}
              placeholder="Rua, número, bairro, cidade/UF"
              className="border-slate-300 focus:border-blue-500"
              data-testid="profile-address-input"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Este é o endereço principal exibido no seu card na Rede de Profissionais.
            </p>
          </div>
        </div>
      </Card>

      {/* Availability */}
      <Card className="p-5 mb-4 border-slate-200">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" /> Disponibilidade
          </h3>
          <div className="flex gap-1 text-[10px]">
            <button onClick={selectWeekdays} className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid="preset-weekdays">
              Seg-Sex
            </button>
            <button onClick={selectAll} className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid="preset-all">
              Todos
            </button>
            <button onClick={clearDays} className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold" data-testid="preset-clear">
              Limpar
            </button>
          </div>
        </div>

        {/* Weekday selector */}
        <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide mb-2">
          Dias que atendo
        </p>
        <div className="grid grid-cols-7 gap-1.5 mb-5">
          {WEEKDAYS.map((d) => {
            const active = form.availability_days.includes(d.id);
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => toggleDay(d.id)}
                data-testid={`weekday-${d.id}`}
                className={`py-2 rounded-lg text-[11px] font-bold transition-all ${
                  active
                    ? 'bg-blue-600 text-white shadow-sm ring-1 ring-blue-700'
                    : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {d.short}
              </button>
            );
          })}
        </div>

        {/* Time range */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5" /> Horário inicial
            </Label>
            <Input
              type="time"
              value={form.availability_start}
              onChange={(e) => setForm({ ...form, availability_start: e.target.value })}
              className="border-slate-300 focus:border-blue-500"
              data-testid="profile-start-time"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-1">
              <Clock className="w-3.5 h-3.5" /> Horário final
            </Label>
            <Input
              type="time"
              value={form.availability_end}
              onChange={(e) => setForm({ ...form, availability_end: e.target.value })}
              className="border-slate-300 focus:border-blue-500"
              data-testid="profile-end-time"
            />
          </div>
        </div>
      </Card>

      {/* Sticky save button */}
      <div className="sticky bottom-4 z-10 mt-4">
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="profile-save-btn"
          className="w-full bg-gradient-to-r from-blue-600 to-slate-900 hover:from-blue-700 hover:to-slate-800 text-white font-bold h-12 shadow-lg text-sm uppercase tracking-wide"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Salvar alterações</>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ProfessionalProfilePage;
